import React, { useState, useEffect } from "react";
import {
  UserPlus,
  Mail,
  Shield,
  User,
  X,
  UserCheck,
  List,
  Grid,
  Trash2,
  Edit2,
  Archive,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../contexts/PermissionsContext";
import { useViewMode } from "../hooks/useViewMode";
import { ImpersonationLoadingDialog } from "./ImpersonationLoadingDialog";
import { useTranslation } from "../i18n/hooks/useTranslation";
import { useNavigation } from "../contexts/NavigationContext";
import { useUpgradeFlow } from "../hooks/useUpgradeFlow";
import { getUserManagementStatus } from "../utils/statusColors";
import { ViewToggle } from "./common/ViewToggle";

interface UserManagementProps {
  onClose?: () => void;
  mode?: "modal" | "page";
}

interface UserData {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string; // System role (System Administrator, Administrator)
  accountRole: string; // Role name from Role model (Account Owner, Account User, or custom role name)
  roleId?: string | { _id: string; name: string }; // Custom role ID (can be string or populated object)
  permissions: string[];
  isActive: boolean;
  isPending?: boolean;
  lastLogin?: Date;
  profileImage?: string;
  twoFactorEnabled?: boolean; // 2FA status
}

interface StaffMember {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  userId?: string;
}

export const UserManagement: React.FC<UserManagementProps> = ({
  onClose,
  mode = "modal",
}) => {
  const { user: currentUser, impersonate } = useAuth();
  const { refetchPermissions } = usePermissions();
  const { navigate, currentPath } = useNavigation();
  const { handleApiError } = useUpgradeFlow();

  // Read URL query parameters
  useEffect(() => {
    if (mode === "page" && currentPath.startsWith("/users")) {
      const urlParams = new URLSearchParams(window.location.search);
      console.log(
        "[UserManagement] Reading URL parameters:",
        Object.fromEntries(urlParams.entries()),
      );
      // Add any user management-specific URL parameter handling here
    }
  }, [currentPath, mode]);

  const [view, setView] = useState<"list" | "invite" | "edit">("list");
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const { viewMode, setViewMode, isMobile } = useViewMode();
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatingUserName, setImpersonatingUserName] = useState("");

  // Connect Staff modal state
  const [showConnectStaffModal, setShowConnectStaffModal] = useState(false);
  const [selectedUserForStaffConnection, setSelectedUserForStaffConnection] =
    useState<UserData | null>(null);
  const [searchStaffQuery, setSearchStaffQuery] = useState("");

  // Create Staff modal state
  const [showCreateStaffModal, setShowCreateStaffModal] = useState(false);
  const [createStaffForm, setCreateStaffForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    hourlyCostRate: "",
  });
  const [openStaffDropdown, setOpenStaffDropdown] = useState<string | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openStaffDropdown) {
        const target = event.target as HTMLElement;
        // Check if click is outside the dropdown container
        if (!target.closest('[data-dropdown-container]')) {
          setOpenStaffDropdown(null);
        }
      }
    };

    if (openStaffDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [openStaffDropdown]);

  // Invite form state
  const [emails, setEmails] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [accounts, setAccounts] = useState<
    Array<{ _id: string; companyName: string }>
  >([]);
  const [availableRolesForInvite, setAvailableRolesForInvite] = useState<
    Array<{ _id: string; name: string }>
  >([]);

  // Edit user state
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    userType: "Account User",
    roleId: "",
    staffId: "",
    accountId: "",
    originalStaffId: "", // Track original staff connection
  });
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [availableRoles, setAvailableRoles] = useState<
    Array<{ _id: string; name: string }>
  >([]);

  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserData | null>(null);

  // User status filter: 'active', 'inactive', or 'pending'
  const [userStatusFilter, setUserStatusFilter] = useState<
    "active" | "inactive" | "pending"
  >("active");

  const { t } = useTranslation();

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Format last login as compact single-line string
  const formatLastLogin = (lastLogin?: Date | string): string => {
    if (!lastLogin) return "Never";

    const now = new Date();
    const loginDate =
      typeof lastLogin === "string" ? new Date(lastLogin) : lastLogin;
    const diffMs = now.getTime() - loginDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const remainingHours = Math.floor((diffMs % 86400000) / 3600000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60)
      return `${diffMins} ${diffMins === 1 ? "min" : "mins"} ago`;
    if (diffHours < 24)
      return `${diffHours} ${diffHours === 1 ? "hr" : "hrs"} ago`;
    if (diffDays === 1 && remainingHours === 0) return "1 day ago";
    if (diffDays > 0 && remainingHours > 0) {
      return `${diffDays}d ${remainingHours}h ago`;
    }
    return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
  };
  const isSystemAdmin = currentUser?.role === "System Administrator";
  const isAdministrator = currentUser?.role === "Administrator";
  // Check account-level role - handle both 'Account Owner' and 'Owner' values
  const isAccountOwner =
    currentUser?.accountRole === "Account Owner" ||
    currentUser?.accountRole === "Owner";

  useEffect(() => {
    if (view === "list") {
      fetchUsers();
      fetchStaff(); // Fetch staff to display connected staff information
    } else if (view === "invite") {
      fetchRolesForInvite();
      if (isAdministrator || isSystemAdmin) {
        fetchAccounts();
      }
    } else if (view === "edit") {
      fetchStaff();
      fetchRolesForEdit();
      if (isSystemAdmin) {
        fetchAccounts();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, isAdministrator, isSystemAdmin, userStatusFilter]);

  const fetchAccounts = async () => {
    // Only fetch accounts for System Admins and Administrators
    if (currentUser?.role !== "System Administrator" && currentUser?.role !== "Administrator") {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/accounts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setAccounts(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
    }
  };

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");

      // Fetch users based on status filter
      const statusParam =
        userStatusFilter === "inactive"
          ? "?status=inactive"
          : userStatusFilter === "pending"
            ? "?status=pending"
            : "?status=active";
      const response = await fetch(`${apiUrl}/api/users${statusParam}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        // Fetch 2FA status for each user
        const usersWithStatus = await Promise.all(
          data.data.map(async (u: any) => {
            // Fetch 2FA status for each user (if admin/account owner)
            let twoFactorEnabled = false;
            if (isSystemAdmin || isAccountOwner || isAdministrator) {
              try {
                const twoFAResponse = await fetch(
                  `${apiUrl}/api/auth/2fa/status?userId=${u._id}`,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                    },
                  },
                );
                const twoFAData = await twoFAResponse.json();
                if (twoFAData.success) {
                  twoFactorEnabled = twoFAData.twoFactorEnabled || false;
                }
              } catch (error) {
                // Silently fail - 2FA status is optional
                console.warn(
                  `Failed to fetch 2FA status for user ${u._id}:`,
                  error,
                );
              }
            }

            return {
              ...u,
              twoFactorEnabled,
            };
          }),
        );

        setUsers(usersWithStatus);
      } else {
        setMessage(data.message || "Failed to fetch users");
      }
    } catch (error) {
      setMessage("Failed to fetch users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/staff`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setStaffMembers(data.data);
        return data.data; // Return the staff data for immediate use
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch staff:", error);
      return [];
    }
  };

  const handleConnectStaffClick = (user: UserData) => {
    setSelectedUserForStaffConnection(user);
    setShowConnectStaffModal(true);
    // Fetch available staff (those without a userId connection)
    fetchStaff();
  };

  const handleCreateStaffClick = (user: UserData) => {
    setSelectedUserForStaffConnection(user);
    // Pre-populate form with user data
    setCreateStaffForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phone: "", // User model doesn't always have phone
      hourlyCostRate: "",
    });
    setShowCreateStaffModal(true);
  };

  const handleCreateStaffSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserForStaffConnection) return;

    if (!createStaffForm.firstName || !createStaffForm.lastName) {
      setMessage("First name and last name are required");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");

      // Create staff with userId connection
      const response = await fetch(`${apiUrl}/api/staff`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: createStaffForm.firstName,
          lastName: createStaffForm.lastName,
          email: createStaffForm.email || undefined,
          phone: createStaffForm.phone || undefined,
          hourlyCostRate:
            createStaffForm.hourlyCostRate.trim() === ""
              ? undefined
              : Number(createStaffForm.hourlyCostRate),
          userId: selectedUserForStaffConnection._id, // Auto-connect to user
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage("Staff created and connected successfully");
        fetchUsers();
        fetchStaff();
        setShowCreateStaffModal(false);
        setSelectedUserForStaffConnection(null);
        setCreateStaffForm({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          hourlyCostRate: "",
        });
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage(data.message || "Failed to create staff");
      }
    } catch (error) {
      console.error("Create staff error:", error);
      setMessage("Failed to create staff");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectStaff = async (staffId: string) => {
    if (!selectedUserForStaffConnection) return;

    setIsLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/staff/${staffId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: selectedUserForStaffConnection._id,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage("Staff connected successfully");
        fetchUsers();
        fetchStaff();
        setShowConnectStaffModal(false);
        setSelectedUserForStaffConnection(null);
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage(data.message || "Failed to connect staff");
      }
    } catch (error) {
      setMessage("Failed to connect staff");
    } finally {
      setIsLoading(false);
    }
  };


  const fetchRolesForInvite = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setAvailableRolesForInvite(data.data);
        // Auto-select first role if available and none selected
        if (data.data.length > 0 && !selectedRoleId) {
          setSelectedRoleId(data.data[0]._id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    }
  };

  const fetchRolesForEdit = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setAvailableRoles(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    }
  };

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();

    // Parse emails from textarea (split by newlines, commas, or semicolons)
    const emailList = emails
      .split(/[\n,;]/)
      .map((email) => email.trim())
      .filter((email) => {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return email.length > 0 && emailRegex.test(email);
      });

    if (emailList.length === 0) {
      setMessage("Please enter at least one valid email address");
      return;
    }

    // Check for duplicates
    const uniqueEmails = Array.from(new Set(emailList));
    if (uniqueEmails.length !== emailList.length) {
      setMessage("Duplicate email addresses found. Please remove duplicates.");
      return;
    }

    // Validate role selection
    if (!selectedRoleId) {
      setMessage(t("userManagement.roleRequired"));
      return;
    }

    // Validate account selection
    if (isAdministrator || isSystemAdmin) {
      if (!selectedAccountId) {
        setMessage("Please select an account");
        return;
      }
    } else if (isAccountOwner) {
      if (!currentUser?.currentAccountId) {
        setMessage(
          "Account ID is required. Please ensure you are associated with an account.",
        );
        return;
      }
    }

    setIsLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      const accountId = isAccountOwner
        ? currentUser?.currentAccountId
        : selectedAccountId;

      // Send invitations for each email
      const results = {
        success: [] as string[],
        failed: [] as Array<{ email: string; error: string }>,
      };

      for (const email of uniqueEmails) {
        try {
          const payload: any = {
            email,
            role: "Account User", // Always Account User
            accountId: accountId,
          };

          // Include roleId if selected
          if (selectedRoleId) {
            payload.roleId = selectedRoleId;
          }

          const response = await fetch(`${apiUrl}/api/auth/send-invitation`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          });

          const data = await response.json();

          // Check if this is a limit error and show upgrade modal
          const wasLimitError = handleApiError(response, data);
          if (wasLimitError) {
            // Stop processing remaining emails if limit error occurs
            results.failed.push({
              email,
              error: data.message || "Subscription limit reached",
            });
            break; // Exit the loop to prevent further invitations
          } else if (data.success) {
            results.success.push(email);
          } else {
            results.failed.push({
              email,
              error: data.message || "Failed to send invitation",
            });
          }
        } catch (error) {
          results.failed.push({ email, error: "Network error" });
        }
      }

      // Show results
      if (results.success.length > 0 && results.failed.length === 0) {
        setMessage(
          `Successfully sent ${results.success.length} invitation${results.success.length > 1 ? "s" : ""}!`,
        );
        setEmails("");
        setSelectedRoleId("");
        setSelectedAccountId("");
        setTimeout(() => {
          setView("list");
          setMessage("");
          fetchUsers();
        }, 2000);
      } else if (results.success.length > 0 && results.failed.length > 0) {
        setMessage(
          `Sent ${results.success.length} invitation${results.success.length > 1 ? "s" : ""}, ${results.failed.length} failed. ${results.failed.map((f) => `${f.email}: ${f.error}`).join("; ")}`,
        );
      } else {
        setMessage(
          `Failed to send invitations. ${results.failed.map((f) => `${f.email}: ${f.error}`).join("; ")}`,
        );
      }
    } catch (error) {
      setMessage("Failed to send invitations. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImpersonate = async (userId: string) => {
    // Find the user we're impersonating for the loading dialog
    const targetUser = users.find((u) => u._id === userId);
    const targetUserName = targetUser
      ? `${targetUser.firstName} ${targetUser.lastName}`
      : "user";

    try {
      setIsImpersonating(true);
      setImpersonatingUserName(targetUserName);

      // The impersonate function now handles all the reloading and redirects
      await impersonate(userId);

      // Note: The page will redirect to /dashboard automatically after successful impersonation
      // So the code below may not execute
      setMessage("Successfully impersonating user");
    } catch (error) {
      setIsImpersonating(false);
      setImpersonatingUserName("");
      setMessage("Failed to impersonate user");
    }
  };

  const handleEditUser = async (user: UserData) => {
    setEditingUser(user);

    // Ensure staff data is loaded before finding connection
    let currentStaffMembers = staffMembers;
    if (currentStaffMembers.length === 0) {
      currentStaffMembers = await fetchStaff();
    }

    // Find connected staff member for this user
    const connectedStaff = currentStaffMembers.find((s) => {
      if (!s.userId) return false;

      // Handle string userId
      if (typeof s.userId === "string") {
        return s.userId.toString() === user._id.toString();
      }

      // Handle populated userId object
      if (typeof s.userId === "object" && "_id" in s.userId) {
        return (s.userId as any)._id.toString() === user._id.toString();
      }

      return false;
    });

    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      userType: user.accountRole || "Account User",
      roleId: (typeof user.roleId === 'object' && user.roleId?._id) ? user.roleId._id : (typeof user.roleId === 'string' ? user.roleId : ""),
      staffId: connectedStaff?._id || "",
      accountId: "",
      originalStaffId: connectedStaff?._id || "", // Track original connection for disconnect
    });
    setView("edit");
  };

  const handleDeleteUser = (userId: string) => {
    const user = users.find((u) => u._id === userId);
    if (user) {
      setUserToDelete(user);
      setShowDeleteDialog(true);
    }
  };



  const handleConfirmDelete = async (permanent: boolean) => {
    if (!userToDelete) return;

    setIsLoading(true);
    setMessage("");
    setShowDeleteDialog(false);

    try {
      const token = localStorage.getItem("token");
      // System Admins and Account Owners can permanently delete
      const permanentParam = permanent ? "?permanent=true" : "";
      const response = await fetch(
        `${apiUrl}/api/users/${userToDelete._id}${permanentParam}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (data.success) {
        setMessage(
          permanent
            ? t("user.deleteUserSuccess")
            : t("user.deactivateUserSuccess"),
        );
        fetchUsers();
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage(data.message || t("user.deleteUserFailed"));
      }
    } catch (error) {
      setMessage(t("user.deleteUserFailed"));
    } finally {
      setIsLoading(false);
      setUserToDelete(null);
    }
  };

  const handleReactivateUser = async (userId: string) => {
    setIsLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/users/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isActive: true }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage("User reactivated successfully");
        fetchUsers();
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage(data.message || "Failed to reactivate user");
      }
    } catch (error) {
      setMessage("Failed to reactivate user. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReinvite = async (userId: string) => {
    setIsLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/auth/reinvite/${userId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setMessage(
          t("userManagement.reinviteSuccess") ||
            "Invitation resent successfully",
        );
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage(
          data.message ||
            t("userManagement.reinviteFailed") ||
            "Failed to resend invitation",
        );
      }
    } catch (error) {
      setMessage(
        t("userManagement.reinviteFailed") ||
          "Failed to resend invitation. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");

      // Update user profile
      const profileResponse = await fetch(
        `${apiUrl}/api/users/${editingUser._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            firstName: editForm.firstName,
            lastName: editForm.lastName,
            email: editForm.email,
          }),
        },
      );

      if (!profileResponse.ok) {
        throw new Error("Failed to update user profile");
      }

      // Change user account if System Admin and accountId changed
      if (isSystemAdmin && editForm.accountId) {
        const accountResponse = await fetch(
          `${apiUrl}/api/users/${editingUser._id}/account`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              accountId: editForm.accountId,
              roleId: editForm.roleId || null,
            }),
          },
        );

        if (!accountResponse.ok) {
          throw new Error("Failed to change user account");
        }
      } else {
        // Update user account role
        const roleResponse = await fetch(
          `${apiUrl}/api/users/${editingUser._id}/role`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              roleId: editForm.roleId || null,
            }),
          },
        );

        if (!roleResponse.ok) {
          throw new Error("Failed to update user role");
        }
      }

      // Handle staff connection changes
      const originalStaffId = editForm.originalStaffId || "";
      const newStaffId = editForm.staffId || "";

      // If staff connection changed
      if (originalStaffId !== newStaffId) {
        // Disconnect old staff if there was one
        if (originalStaffId) {
          const disconnectResponse = await fetch(
            `${apiUrl}/api/staff/${originalStaffId}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                userId: null,
              }),
            },
          );

          if (!disconnectResponse.ok) {
            console.warn("Failed to disconnect old staff, but continuing...");
          }
        }

        // Connect new staff if selected
        if (newStaffId) {
          const staffResponse = await fetch(
            `${apiUrl}/api/staff/${newStaffId}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                userId: editingUser._id,
              }),
            },
          );

          if (!staffResponse.ok) {
            throw new Error("Failed to update staff connection");
          }
        }
      }

      // Refresh permissions if current user was edited or if they are affected by role changes
      if (editingUser._id === currentUser?.id) {
        await refetchPermissions();
      }

      setMessage("User updated successfully");
      setTimeout(() => {
        setView("list");
        setMessage("");
        fetchUsers();
      }, 2000);
    } catch (error) {
      setMessage("Failed to update user");
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "System Administrator":
        return <Shield className="w-4 h-4 text-error-600" />;
      case "Administrator":
        return <Shield className="w-4 h-4 text-purple-600" />;
      case "Account Owner":
        return <Shield className="w-4 h-4 text-primary-600" />;
      case "Account User":
        return <User className="w-4 h-4 text-accent-600" />;
      default:
        // Custom roles get a distinct icon
        return <Shield className="w-4 h-4 text-success-600" />;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "System Administrator":
        return "bg-error-100 dark:bg-error-800 text-error-700 dark:text-error-300";
      case "Administrator":
        return "bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-300";
      case "Account Owner":
        return "bg-primary-100 dark:bg-primary-800 text-primary-700 dark:text-primary-300";
      case "Account User":
        return "bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300";
      default:
        // Custom roles get a distinct green/success color
        return "bg-success-100 dark:bg-success-800 text-success-700 dark:text-success-300";
    }
  };



  const renderEditForm = () => (
    <form onSubmit={handleSaveEdit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="editFirstName"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            First Name
          </label>
          <input
            type="text"
            id="editFirstName"
            value={editForm.firstName}
            onChange={(e) =>
              setEditForm({ ...editForm, firstName: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            required
          />
        </div>

        <div>
          <label
            htmlFor="editLastName"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Last Name
          </label>
          <input
            type="text"
            id="editLastName"
            value={editForm.lastName}
            onChange={(e) =>
              setEditForm({ ...editForm, lastName: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            required
          />
        </div>
      </div>

      <div>
        <label
          htmlFor="editEmail"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Email
        </label>
        <input
          type="email"
          id="editEmail"
          value={editForm.email}
          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          required
        />
      </div>

      {isSystemAdmin && (
        <div>
          <label
            htmlFor="editAccount"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t("user.userAccount")}
          </label>
          <select
            id="editAccount"
            value={editForm.accountId}
            onChange={(e) =>
              setEditForm({ ...editForm, accountId: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="">{t("user.selectUserAccount")}</option>
            {accounts.map((account) => (
              <option key={account._id} value={account._id}>
                {account.companyName}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label
          htmlFor="editRole"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Role
        </label>
        <select
          id="editRole"
          value={editForm.roleId}
          onChange={(e) => setEditForm({ ...editForm, roleId: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          disabled={!isAccountOwner && !isSystemAdmin}
        >
          <option value="">None (default permissions)</option>
          {availableRoles.map((role) => (
            <option key={role._id} value={role._id}>
              {role.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor="editStaffConnection"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Staff Connection
        </label>
        <select
          id="editStaffConnection"
          value={editForm.staffId}
          onChange={(e) =>
            setEditForm({ ...editForm, staffId: e.target.value })
          }
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
        >
          <option value="">None</option>
          {staffMembers.map((staff) => (
            <option key={staff._id} value={staff._id}>
              {staff.firstName} {staff.lastName}{" "}
              {staff.email && `(${staff.email})`}
            </option>
          ))}
        </select>
      </div>

      {message && (
        <div
          className={`p-3 rounded-md text-sm ${
            message.includes("successfully") || message.includes("Successfully")
              ? "bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300"
              : "bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300"
          }`}
        >
          {message}
        </div>
      )}

      <div className="flex space-x-3 pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
        >
          {isLoading ? "Saving..." : "Save Changes"}
        </button>
        <button
          type="button"
          onClick={() => {
            setView("list");
            setMessage("");
          }}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          Cancel
        </button>
      </div>
    </form>
  );

  const renderInviteForm = () => (
    <form onSubmit={handleSendInvitation} className="space-y-4">
      <div>
        <label
          htmlFor="emails"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Email Addresses{" "}
          <span className="text-gray-500 text-xs">
            (one per line, or separated by commas)
          </span>
        </label>
        <textarea
          id="emails"
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
          rows={5}
          required
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Enter multiple email addresses, one per line or separated by commas
        </p>
      </div>

      <div>
        <label
          htmlFor="role"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {t("userManagement.role")} <span className="text-error-500">*</span>
        </label>
        {availableRolesForInvite.length === 0 ? (
          <div className="w-full px-3 py-2 border border-yellow-300 dark:border-yellow-600 rounded-md bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50">
            <p className="text-sm text-warning-800 dark:text-warning-200 mb-2">
              {t("userManagement.noRolesAvailable")}
            </p>
            <button
              type="button"
              onClick={() => navigate("/admin?tab=roles")}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline font-medium"
            >
              {t("userManagement.createRole")} →
            </button>
          </div>
        ) : (
          <>
            <select
              id="role"
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              required
            >
              {availableRolesForInvite.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t("userManagement.roleRequiredDescription")}
            </p>
          </>
        )}
      </div>

      {(isAdministrator || isSystemAdmin) && (
        <div>
          <label
            htmlFor="account"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Select Account <span className="text-error-500">*</span>
          </label>
          <select
            id="account"
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            required
          >
            <option value="">Select an account</option>
            {accounts.map((account) => (
              <option key={account._id} value={account._id}>
                {account.companyName}
              </option>
            ))}
          </select>
        </div>
      )}

      {message && (
        <div
          className={`p-3 rounded-md text-sm ${
            message.includes("successfully") || message.includes("Successfully")
              ? "bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300"
              : "bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300"
          }`}
        >
          {message}
        </div>
      )}

      <div className="flex space-x-3 pt-4">
        <button
          type="submit"
          disabled={
            isLoading || availableRolesForInvite.length === 0 || !selectedRoleId
          }
          className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          <Mail className="w-4 h-4" />
          <span>{isLoading ? "Sending..." : "Send Invitation"}</span>
        </button>
        <button
          type="button"
          onClick={() => {
            setView("list");
            setMessage("");
            setEmails("");
            setSelectedRoleId("");
            setSelectedAccountId("");
          }}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          Back
        </button>
      </div>
    </form>
  );

  // If in page mode and showing invite, render as modal overlay
  if (mode === "page" && view === "invite") {
    return (
      <>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              User Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage users and permissions
            </p>
          </div>
        </div>

        {/* Invite Modal Overlay */}
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Invite New User
              </h3>
              <button
                onClick={() => setView("list")}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {renderInviteForm()}
          </div>
        </div>
      </>
    );
  }

  const containerClasses =
    mode === "page"
      ? "p-6 max-w-7xl mx-auto"
      : "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50";

  const contentClasses =
    mode === "page"
      ? "w-full"
      : "bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto";

  return (
    <>
      {isImpersonating && (
        <ImpersonationLoadingDialog targetUserName={impersonatingUserName} />
      )}

      <div className={containerClasses}>
        <div className={contentClasses}>
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200 dark:border-gray-600">
            {mode === "page" ? (
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  User Management
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Manage users and permissions
                </p>
              </div>
            ) : (
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {view === "list" ? "User Management" : "Invite New User"}
              </h3>
            )}
            {onClose && mode !== "page" && (
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {view === "list" ? (
            <>
              <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Total Users:{" "}
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {users.length}
                    </span>
                  </p>
                  <ViewToggle
                    viewMode={userStatusFilter}
                    onViewModeChange={setUserStatusFilter}
                    options={[
                      { value: 'active', label: 'Active', title: 'Show active users' },
                      { value: 'inactive', label: 'Inactive', title: 'Show inactive users' },
                      { value: 'pending', label: 'Pending', title: 'Show pending users' }
                    ]}
                  />
                </div>
                <div className="flex items-center gap-2">
                  {/* View Toggle - Hide on mobile */}
                  {!isMobile && (
                    <ViewToggle
                      viewMode={viewMode}
                      onViewModeChange={setViewMode}
                      options={[
                        { value: 'list', icon: List, title: 'List view' },
                        { value: 'cards', icon: Grid, title: 'Card view' }
                      ]}
                      iconSize={16}
                    />
                  )}
                  <button
                    onClick={() => setView("invite")}
                    className="bg-primary-600 text-white px-3 py-1.5 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center gap-1.5 text-sm font-medium"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>Invite User</span>
                  </button>
                </div>
              </div>

              {message && (
                <div
                  className={`p-3 rounded-md text-sm mb-4 ${
                    message.includes("success") ||
                    message.includes("Successfully")
                      ? "bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300"
                      : "bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300"
                  }`}
                >
                  {message}
                </div>
              )}

              {isLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <p className="text-gray-600 dark:text-gray-400 mt-2">
                    Loading users...
                  </p>
                </div>
              ) : viewMode === "list" ? (
                <div className="overflow-x-auto -mx-6 px-6">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-600">
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                          Name
                        </th>
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                          Email
                        </th>
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                          User Type
                        </th>
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                          Connected Staff
                        </th>
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                          Status
                        </th>
                        <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          Last Login
                        </th>
                        {/* 2FA column hidden - now handled by 3PM Auth */}
                        {(isSystemAdmin || isAccountOwner) && (
                          <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {users.map((user) => {
                        // Impersonation rules:
                        // - Only System Admins can impersonate users
                        // - System Admins can impersonate anyone except other System Admins
                        // - Only active users can be impersonated (not pending or inactive)
                        // - Cannot impersonate yourself
                        const canImpersonate =
                          isSystemAdmin &&
                          user._id !== currentUser?.id &&
                          user.isActive &&
                          !user.isPending &&
                          user.role !== "System Administrator";

                        return (
                          <tr
                            key={user._id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            <td className="py-2.5 px-3">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                                  {user.profileImage ? (
                                    <img
                                      src={user.profileImage}
                                      alt="Profile"
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <User className="w-4 h-4 text-white" />
                                  )}
                                </div>
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {user.firstName} {user.lastName}
                                </span>
                              </div>
                            </td>
                            <td className="py-2.5 px-3">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {user.email}
                              </span>
                            </td>
                            <td className="py-2.5 px-3">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium w-fit ${getRoleBadgeColor(user.role === "System Administrator" || user.role === "Administrator" ? user.role : user.accountRole)}`}
                              >
                                {user.role === "System Administrator" ||
                                user.role === "Administrator"
                                  ? user.role
                                  : user.accountRole}
                              </span>
                            </td>
                            <td className="py-2.5 px-3">
                              {(() => {
                                const connectedStaff = staffMembers.find(
                                  (s) => {
                                    if (!s.userId) return false;

                                    // Handle string userId
                                    if (typeof s.userId === "string") {
                                      return (
                                        s.userId.toString() ===
                                        user._id.toString()
                                      );
                                    }

                                    // Handle populated userId object
                                    if (
                                      typeof s.userId === "object" &&
                                      "_id" in s.userId
                                    ) {
                                      return (
                                        (s.userId as any)._id.toString() ===
                                        user._id.toString()
                                      );
                                    }

                                    return false;
                                  },
                                );
                                return connectedStaff ? (
                                  <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                                      <User className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                        {connectedStaff.firstName}{" "}
                                        {connectedStaff.lastName}
                                      </span>
                                      {connectedStaff.email && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                          {connectedStaff.email}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() =>
                                      handleConnectStaffClick(user)
                                    }
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                                    title="Connect Staff"
                                  >
                                    <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                                      <User className="w-2.5 h-2.5 text-gray-600 dark:text-gray-400" />
                                    </div>
                                    <span>Connect Staff</span>
                                  </button>
                                );
                              })()}
                            </td>
                            <td className="py-2.5 px-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${getUserManagementStatus(user.isPending || false, user.isActive)}`}
                          >
                            {user.isPending
                              ? "Pending"
                              : user.isActive
                                ? "Active"
                                : "Inactive"}
                          </span>
                            </td>
                            <td className="py-2.5 px-3">
                              <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                {formatLastLogin(user.lastLogin)}
                              </span>
                            </td>
                            {/* 2FA status cell hidden - now handled by 3PM Auth */}
                            {(isSystemAdmin || isAccountOwner) && (
                              <td className="py-2.5 px-3">
                                <div className="flex items-center justify-end gap-0">
                                  <button
                                    onClick={() => handleEditUser(user)}
                                    className="p-1.5 text-accent-600 dark:text-accent-400 hover:text-accent-900 dark:hover:text-accent-300 transition-colors rounded hover:bg-accent-50 dark:hover:bg-accent-900/20"
                                    title={t("common.edit")}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  {/* 2FA reset button hidden - now handled by 3PM Auth */}
                                  {user.isPending && (
                                    <button
                                      onClick={() => handleReinvite(user._id)}
                                      disabled={isLoading}
                                      className="p-1.5 text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300 disabled:opacity-50 transition-colors rounded hover:bg-purple-50 dark:hover:bg-purple-900/20"
                                      title={
                                        t("userManagement.reinvite") ||
                                        "Resend Invitation"
                                      }
                                    >
                                      <Mail className="w-4 h-4" />
                                    </button>
                                  )}
                                  {!user.isActive && !user.isPending && (
                                    <button
                                      onClick={() =>
                                        handleReactivateUser(user._id)
                                      }
                                      disabled={isLoading}
                                      className="p-1.5 text-success-600 dark:text-success-400 hover:text-success-900 dark:hover:text-success-300 disabled:opacity-50 transition-colors rounded hover:bg-success-50 dark:hover:bg-success-900/20"
                                      title="Reactivate User"
                                    >
                                      <RotateCcw className="w-4 h-4" />
                                    </button>
                                  )}
                                  {canImpersonate && (
                                    <button
                                      onClick={() =>
                                        handleImpersonate(user._id)
                                      }
                                      disabled={isLoading}
                                      className="inline-flex items-center space-x-1 px-2.5 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 transition-colors"
                                      title={t("user.impersonateTooltip")}
                                    >
                                      <UserCheck className="w-3 h-3" />
                                      <span>{t("user.impersonate")}</span>
                                    </button>
                                  )}
                                  {(() => {
                                    // System Admins can delete any non-System Admin user
                                    // Account Owners can delete Account Users (not Account Owners or System Admins)
                                    // Inactive users can still be permanently deleted
                                    const canDelete = isSystemAdmin
                                      ? user.role !== "System Administrator"
                                      : isAccountOwner
                                        ? user.accountRole === "Account User" &&
                                          user.role !==
                                            "System Administrator" &&
                                          user.role !== "Administrator"
                                        : false;

                                    return (
                                      canDelete && (
                                        <button
                                          onClick={() =>
                                            handleDeleteUser(user._id)
                                          }
                                          disabled={isLoading}
                                          className="p-1.5 text-error-600 dark:text-error-400 hover:text-error-900 dark:hover:text-error-300 disabled:opacity-50 transition-colors rounded hover:bg-error-50 dark:hover:bg-error-900/20"
                                          title={t("common.delete")}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      )
                                    );
                                  })()}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {users.length === 0 && (
                    <tr>
                      <td
                        colSpan={
                          6 +
                          (isSystemAdmin || isAccountOwner || isAdministrator
                            ? 1
                            : 0) +
                          (isSystemAdmin || isAccountOwner ? 1 : 0)
                        }
                        className="py-8 text-center"
                      >
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                          No users found
                        </p>
                      </td>
                    </tr>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {users.map((user) => (
                    <div
                      key={user._id}
                      className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {user.profileImage ? (
                              <img
                                src={user.profileImage}
                                alt="Profile"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <User className="w-6 h-6 text-white" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                              {user.firstName} {user.lastName}
                            </h3>
                            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 mb-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Role:
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role === "System Administrator" || user.role === "Administrator" ? user.role : user.accountRole)}`}
                          >
                            {getRoleIcon(
                              user.role === "System Administrator" ||
                                user.role === "Administrator"
                                ? user.role
                                : user.accountRole,
                            )}
                            <span className="ml-1">
                              {user.role === "System Administrator" ||
                              user.role === "Administrator"
                                ? user.role
                                : user.accountRole}
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Status:
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getUserManagementStatus(user.isPending || false, user.isActive)}`}
                          >
                            {user.isPending
                              ? "Pending"
                              : user.isActive
                                ? "Active"
                                : "Inactive"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Last Login:
                          </span>
                          <span className="text-xs text-gray-900 dark:text-gray-100">
                            {formatLastLogin(user.lastLogin)}
                          </span>
                        </div>
                      </div>

                      {(isSystemAdmin || isAccountOwner) && (
                        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                          {user.isPending && (
                            <button
                              onClick={() => handleReinvite(user._id)}
                              disabled={isLoading}
                              className="text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300 disabled:opacity-50"
                              title={
                                t("userManagement.reinvite") ||
                                "Resend Invitation"
                              }
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                          )}
                          {!user.isActive && !user.isPending && (
                            <button
                              onClick={() => handleReactivateUser(user._id)}
                              disabled={isLoading}
                              className="text-success-600 dark:text-success-400 hover:text-success-900 dark:hover:text-success-300 disabled:opacity-50"
                              title="Reactivate User"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                          {/* 2FA reset button hidden - now handled by 3PM Auth */}
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-accent-600 dark:text-accent-400 hover:text-accent-900 dark:hover:text-accent-300"
                            title={t("common.edit")}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          {(() => {
                            // Impersonation rules:
                            // - Only System Admins can impersonate users
                            // - System Admins can impersonate anyone except other System Admins
                            // - Only active users can be impersonated (not pending or inactive)
                            // - Cannot impersonate yourself
                            const canImpersonate =
                              isSystemAdmin &&
                              user._id !== currentUser?.id &&
                              user.isActive &&
                              !user.isPending &&
                              user.role !== "System Administrator";

                            return (
                              canImpersonate && (
                                <button
                                  onClick={() => handleImpersonate(user._id)}
                                  disabled={isLoading}
                                  className="flex items-center justify-center space-x-1.5 px-2.5 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 flex-shrink-0 transition-colors"
                                  title={t("user.impersonateTooltip")}
                                >
                                  <UserCheck className="w-4 h-4" />
                                  <span className="whitespace-nowrap">
                                    {t("user.impersonate")}
                                  </span>
                                </button>
                              )
                            );
                          })()}
                          {(() => {
                            // System Admins can delete any non-System Admin user
                            // Account Owners can delete Account Users (not Account Owners or System Admins)
                            // Inactive users can still be permanently deleted
                            const canDelete = isSystemAdmin
                              ? user.role !== "System Administrator"
                              : isAccountOwner
                                ? user.accountRole === "Account User" &&
                                  user.role !== "System Administrator" &&
                                  user.role !== "Administrator"
                                : false;

                            return (
                              canDelete && (
                                <button
                                  onClick={() => handleDeleteUser(user._id)}
                                  disabled={isLoading}
                                  className="text-error-600 dark:text-error-400 hover:text-error-900 dark:hover:text-error-300 disabled:opacity-50"
                                  title={t("common.delete")}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  ))}

                  {users.length === 0 && (
                    <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                      No users found
                    </div>
                  )}
                </div>
              )}
            </>
          ) : view === "invite" ? (
            renderInviteForm()
          ) : (
            renderEditForm()
          )}
        </div>

        {/* Connect Staff Modal */}
        {showConnectStaffModal && selectedUserForStaffConnection && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Connect Staff
                  </h3>
                  <button
                    onClick={() => {
                      setShowConnectStaffModal(false);
                      setSelectedUserForStaffConnection(null);
                      setSearchStaffQuery("");
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Select staff to connect:{" "}
                  {selectedUserForStaffConnection.firstName}{" "}
                  {selectedUserForStaffConnection.lastName}
                </p>
              </div>

              <div className="p-4 border-b border-gray-200 dark:border-gray-600">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchStaffQuery}
                  onChange={(e) => setSearchStaffQuery(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {staffMembers
                  .filter(
                    (s) =>
                      !searchStaffQuery ||
                      `${s.firstName} ${s.lastName}`
                        .toLowerCase()
                        .includes(searchStaffQuery.toLowerCase()) ||
                      (s.email &&
                        s.email
                          .toLowerCase()
                          .includes(searchStaffQuery.toLowerCase())),
                  )
                  .map((staffMember) => (
                    <button
                      key={staffMember._id}
                      onClick={() => handleConnectStaff(staffMember._id)}
                      disabled={isLoading}
                      className="w-full p-3 mb-2 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left disabled:opacity-50"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {staffMember.firstName} {staffMember.lastName}
                          </div>
                          {staffMember.email && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {staffMember.email}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}

                {staffMembers.filter(
                  (s) =>
                    !searchStaffQuery ||
                    `${s.firstName} ${s.lastName}`
                      .toLowerCase()
                      .includes(searchStaffQuery.toLowerCase()) ||
                    (s.email &&
                      s.email
                        .toLowerCase()
                        .includes(searchStaffQuery.toLowerCase())),
                ).length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No staff found
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-gray-200 dark:border-gray-600">
                <button
                  onClick={() => {
                    setShowConnectStaffModal(false);
                    handleCreateStaffClick(selectedUserForStaffConnection);
                  }}
                  className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>Create New Staff</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Staff Modal */}
        {showCreateStaffModal && selectedUserForStaffConnection && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Create & Connect Staff
                  </h3>
                  <button
                    onClick={() => {
                      setShowCreateStaffModal(false);
                      setSelectedUserForStaffConnection(null);
                      setCreateStaffForm({
                        firstName: "",
                        lastName: "",
                        email: "",
                        phone: "",
                        hourlyCostRate: "",
                      });
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Create a new staff record for:{" "}
                  {selectedUserForStaffConnection.firstName}{" "}
                  {selectedUserForStaffConnection.lastName}
                </p>
              </div>

              <form onSubmit={handleCreateStaffSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="createStaffFirstName"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      First Name <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="createStaffFirstName"
                      value={createStaffForm.firstName}
                      onChange={(e) =>
                        setCreateStaffForm({
                          ...createStaffForm,
                          firstName: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                      placeholder="First Name"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="createStaffLastName"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                      Last Name <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="createStaffLastName"
                      value={createStaffForm.lastName}
                      onChange={(e) =>
                        setCreateStaffForm({
                          ...createStaffForm,
                          lastName: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                      placeholder="Last Name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="createStaffEmail"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="createStaffEmail"
                    value={createStaffForm.email}
                    onChange={(e) =>
                      setCreateStaffForm({
                        ...createStaffForm,
                        email: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                    placeholder="email@example.com"
                  />
                </div>

                <div>
                  <label
                    htmlFor="createStaffPhone"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="createStaffPhone"
                    value={createStaffForm.phone}
                    onChange={(e) =>
                      setCreateStaffForm({
                        ...createStaffForm,
                        phone: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label
                    htmlFor="createStaffHourlyCostRate"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Hourly Cost Rate
                  </label>
                  <input
                    type="number"
                    id="createStaffHourlyCostRate"
                    min={0}
                    step={0.01}
                    value={createStaffForm.hourlyCostRate}
                    onChange={(e) =>
                      setCreateStaffForm({
                        ...createStaffForm,
                        hourlyCostRate: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                    placeholder="0.00"
                  />
                </div>

                {message && (
                  <div
                    className={`p-3 rounded-md text-sm ${
                      message.includes("success")
                        ? "bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300"
                        : "bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300"
                    }`}
                  >
                    {message}
                  </div>
                )}

                <div className="flex space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
                  >
                    {isLoading ? "Creating..." : "Create & Connect"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateStaffModal(false);
                      setSelectedUserForStaffConnection(null);
                      setCreateStaffForm({
                        firstName: "",
                        lastName: "",
                        email: "",
                        phone: "",
                        hourlyCostRate: "",
                      });
                      setMessage("");
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* 2FA Reset Dialog hidden - now handled by 3PM Auth */}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
            {/* Header */}
            <div className="flex justify-between items-start p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-error-100 dark:bg-gray-700 dark:border dark:border-error-800/50 flex items-center justify-center mr-3">
                  <AlertTriangle className="w-6 h-6 text-error-600 dark:text-error-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Delete User
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {userToDelete.firstName} {userToDelete.lastName} (
                    {userToDelete.email})
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setUserToDelete(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Are you sure you want to delete this user? Choose an option
                below:
              </p>

              <div className="space-y-3">
                {/* Deactivate Option */}
                <button
                  onClick={() => handleConfirmDelete(false)}
                  disabled={
                    isLoading ||
                    (!userToDelete.isActive && !userToDelete.isPending)
                  }
                  className={`w-full text-left p-4 border-2 rounded-lg transition-colors ${
                    !userToDelete.isActive && !userToDelete.isPending
                      ? "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 cursor-not-allowed opacity-50"
                      : "border-primary-200 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                  } disabled:opacity-50`}
                  title={
                    !userToDelete.isActive && !userToDelete.isPending
                      ? "User is already inactive"
                      : ""
                  }
                >
                  <div className="flex items-start">
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                        !userToDelete.isActive && !userToDelete.isPending
                          ? "bg-gray-100 dark:bg-gray-700"
                          : "bg-primary-100 dark:bg-gray-700 dark:border dark:border-primary-800/50"
                      }`}
                    >
                      <Archive
                        className={`w-5 h-5 ${
                          !userToDelete.isActive && !userToDelete.isPending
                            ? "text-gray-400 dark:text-gray-500"
                            : "text-primary-600 dark:text-primary-400"
                        }`}
                      />
                    </div>
                    <div className="flex-1">
                      <h4
                        className={`font-semibold mb-1 ${
                          !userToDelete.isActive && !userToDelete.isPending
                            ? "text-gray-400 dark:text-gray-500"
                            : "text-gray-900 dark:text-gray-100"
                        }`}
                      >
                        Deactivate User
                        {!userToDelete.isActive && !userToDelete.isPending && (
                          <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">
                            (Already inactive)
                          </span>
                        )}
                      </h4>
                      <p
                        className={`text-sm ${
                          !userToDelete.isActive && !userToDelete.isPending
                            ? "text-gray-400 dark:text-gray-500"
                            : "text-gray-600 dark:text-gray-400"
                        }`}
                      >
                        {!userToDelete.isActive && !userToDelete.isPending
                          ? "This user is already inactive. Use the reactivate button to restore access."
                          : "The user will be deactivated and cannot log in. This action can be reversed by reactivating the user."}
                      </p>
                    </div>
                  </div>
                </button>

                {/* Permanent Delete Option */}
                <button
                  onClick={() => handleConfirmDelete(true)}
                  disabled={isLoading}
                  className="w-full text-left p-4 border-2 border-red-200 dark:border-red-800 rounded-lg hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-error-100 dark:bg-gray-700 dark:border dark:border-error-800/50 flex items-center justify-center mr-3">
                      <Trash2 className="w-5 h-5 text-error-600 dark:text-error-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        Permanently Delete User
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        The user and all associated data will be permanently
                        deleted. This action cannot be undone.
                      </p>
                      <div className="p-2 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 rounded text-xs text-error-700 dark:text-error-300">
                        ⚠️ Warning: This is a permanent action that cannot be
                        reversed.
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setUserToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
